import { useEffect } from 'react';
import PageLayout from '../../components/feature/PageLayout';
import PageHero from '../../components/feature/PageHero';

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    // SEO Meta Tags - noindex for legal pages
    document.title = 'Privacy Policy | Evernorth AI';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Privacy Policy for Evernorth AI. Learn how we collect, use, and protect your personal information.');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/privacy-policy`);
    }

    // Add noindex meta tag
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.setAttribute('name', 'robots');
      document.head.appendChild(metaRobots);
    }
    metaRobots.setAttribute('content', 'noindex, nofollow');

    return () => {
      if (metaRobots && metaRobots.parentNode) {
        metaRobots.setAttribute('content', 'index, follow');
      }
    };
  }, []);

  return (
    <PageLayout>
      <PageHero
        badge="EVERNORTH AI"
        title="Privacy Policy"
        titleHighlight=""
        description=""
      />
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gold/10 border border-gold/20 rounded-lg p-6 mb-12">
            <p className="text-navy text-sm">
              <strong>Last updated:</strong> January 2026
            </p>
            <p className="text-navy/80 text-sm mt-2">
              Evernorth AI respects your privacy. This policy explains what
              information we collect, how we use it, and how we protect it.
            </p>
          </div>

          <div className="space-y-12">
            {/* 1 – Information We Collect */}
            <div>
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gold rounded-lg text-white font-bold">
                  1
                </span>
                Information We Collect
              </h2>
              <div className="pl-13 space-y-3 text-navy/80 leading-relaxed">
                <p>
                  We collect information that helps us communicate with you and
                  provide our services.
                </p>
                <p>We may collect:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Name</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Organization name</li>
                  <li>Job title</li>
                  <li>Region or service area</li>
                  <li>Details you share in forms or scheduling tools</li>
                  <li>Records of calls or emails</li>
                  <li>Website browsing activity through basic analytics</li>
                </ul>
                <p>
                  <strong className="text-navy">
                    We do not collect sensitive personal information.
                  </strong>
                </p>
              </div>
            </div>

            {/* 2 – How We Use Your Information */}
            <div>
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gold rounded-lg text-white font-bold">
                  2
                </span>
                How We Use Your Information
              </h2>
              <div className="pl-13 space-y-3 text-navy/80 leading-relaxed">
                <p>We use the information you provide to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Respond to inquiries</li>
                  <li>Schedule calls and workshops</li>
                  <li>Deliver training and consulting services</li>
                  <li>Send follow up messages</li>
                  <li>Improve our services and website</li>
                  <li>Maintain internal records</li>
                  <li>Comply with legal requirements</li>
                </ul>
                <p>
                  <strong className="text-navy">
                    We do not sell your data. Ever.
                  </strong>
                </p>
              </div>
            </div>

            {/* 3 – How We Share Information */}
            <div>
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gold rounded-lg text-white font-bold">
                  3
                </span>
                How We Share Information
              </h2>
              <div className="pl-13 space-y-3 text-navy/80 leading-relaxed">
                <p>
                  We only share information with trusted service providers who
                  help us operate the business. Examples include:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Email and calendar tools</li>
                  <li>CRM platforms</li>
                  <li>Scheduling tools</li>
                  <li>Payment processors</li>
                  <li>Website hosting services</li>
                </ul>
                <p>
                  These providers only receive the information needed to perform
                  their functions. We never share or sell information to outside
                  marketers.
                </p>
              </div>
            </div>

            {/* 4 – Cookies and Analytics */}
            <div>
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gold rounded-lg text-white font-bold">
                  4
                </span>
                Cookies and Analytics
              </h2>
              <div className="pl-13 space-y-3 text-navy/80 leading-relaxed">
                <p>
                  Our website may use cookies and basic analytics to understand
                  how visitors use the site. This helps us improve navigation and
                  content.
                </p>
                <p>You can adjust cookie settings in your browser at any time.</p>
              </div>
            </div>

            {/* 5 – Data Storage and Security */}
            <div>
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gold rounded-lg text-white font-bold">
                  5
                </span>
                Data Storage and Security
              </h2>
              <div className="pl-13 space-y-3 text-navy/80 leading-relaxed">
                <p>We take reasonable steps to protect personal information.</p>
                <p>We use secure tools for:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Email</li>
                  <li>Calendar</li>
                  <li>Payments</li>
                  <li>CRM</li>
                  <li>File storage</li>
                </ul>
                <p>
                  While no system is perfect, we work to keep your information
                  safe and confidential. We take privacy and security very
                  seriously.
                </p>
              </div>
            </div>

            {/* 6 – Third Party Links */}
            <div>
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gold rounded-lg text-white font-bold">
                  6
                </span>
                Third Party Links
              </h2>
              <div className="pl-13 space-y-3 text-navy/80 leading-relaxed">
                <p>Our website may contain links to other sites.</p>
                <p>
                  We are not responsible for the content or privacy practices
                  of those sites.
                </p>
              </div>
            </div>

            {/* 7 – Your Choices */}
            <div>
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gold rounded-lg text-white font-bold">
                  7
                </span>
                Your Choices
              </h2>
              <div className="pl-13 space-y-3 text-navy/80 leading-relaxed">
                <p>You may request to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Update your information</li>
                  <li>Correct inaccurate information</li>
                  <li>Ask us to delete your information</li>
                  <li>Opt out of communication</li>
                </ul>
                <p>Contact us at the address below to make a request.</p>
              </div>
            </div>

            {/* 8 – Children’s Privacy */}
            <div>
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gold rounded-lg text-white font-bold">
                  8
                </span>
                Children&#39;s Privacy
              </h2>
              <div className="pl-13 space-y-3 text-navy/80 leading-relaxed">
                <p>
                  We do not knowingly intentionally collect information from
                  children under 13.
                </p>
              </div>
            </div>

            {/* 9 – Updates to This Policy */}
            <div>
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gold rounded-lg text-white font-bold">
                  9
                </span>
                Updates to This Policy
              </h2>
              <div className="pl-13 space-y-3 text-navy/80 leading-relaxed">
                <p>
                  We may update this policy to reflect changes in our services or
                  legal requirements. Updated versions will be posted on this
                  page.
                </p>
              </div>
            </div>

            {/* 10 – Contact Information */}
            <div>
              <h2 className="text-2xl font-bold text-navy mb-4 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gold rounded-lg text-white font-bold">
                  10
                </span>
                Contact Information
              </h2>
              <div className="pl-13 space-y-3 text-navy/80 leading-relaxed">
                <p>If you have questions about this Privacy Policy, contact:</p>
                <div className="bg-navy/5 rounded-lg p-6 mt-4">
                  <p className="font-bold text-navy">Evernorth AI</p>
                  <p className="text-navy/80">info@evernorthai.com</p>
                  <p className="text-navy/80">Northwest Georgia</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 p-8 bg-gold/10 border border-gold/20 rounded-lg">
            <p className="text-navy/80 text-sm leading-relaxed">
              By using our website or services, you acknowledge that you have read
              and understood this Privacy Policy. If you have any concerns about
              how your information is handled, please contact us using the
              information above.
            </p>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
